import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Row, Col } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useSelector } from "react-redux";
import { FiLayers, FiMapPin, FiFileText, FiSend, FiArrowLeft, FiCheck } from "react-icons/fi";

import UploadMedia from "../../../components/media/UploadMedia";
import Notification, { showToast } from "../../../../components/common/notification/Notification";
import { useNavigate } from "react-router-dom";
import styles from "./AddPostForm.module.css";

const MAX_DESCRIPTION_LENGTH = 2000;

const AddPostForm = () => {
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [characterCount, setCharacterCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  const token = user?.refreshToken;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const serviceRes = await axios.get(`${baseUrl}/all/services`);
        setServices(serviceRes.data.services);

        const stateRes = await axios.post(
          `${baseUrl}/admin/states/1`,
          { page_number: 1, page_size: 20, order_by: "ASC" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStates(stateRes.data.states);
      } catch {
        console.error("Error fetching services and states");
      }
    };
    fetchData();
  }, [baseUrl, token]);

  const fetchCities = async (stateId, setFieldValue) => {
    setFieldValue("city", "");
    if (!stateId) {
      setCities([]);
      return;
    }
    try {
      const res = await axios.post(
        `${baseUrl}/admin/cities/${stateId}`,
        { page_number: 1, page_size: 20, order_by: "ASC" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCities(res.data.cities || []);
    } catch {
      setCities([]);
    }
  };

  const validationSchema = Yup.object({
    service: Yup.string().required("Service is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    description: Yup.string()
      .required("Description is required")
      .max(MAX_DESCRIPTION_LENGTH, "Description too long"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setPreviewData(values);
    setShowPreview(true);
    setSubmitting(false);
  };

  const handleFinalSubmit = async () => {
    const imagesToSend = images.map((img) => ({
      image_url: img.image_url,
      imagePath: img.imagePath,
    }));
    const videosToSend = videos.map((video) => ({
      url_path: video.image_url,
    }));
    const attachmentsToSend = attachments.map((att) => ({
      attachment_url: att.url,
    }));

    const currentUserId = user?.id || user?.user?.id || user?.userDetails?.id;

    const payload = {
      country_name: "India",
      service: previewData.service,
      state_name: previewData.state_name,
      city_name: previewData.city_name,
      description: previewData.description,
      images: imagesToSend,
      videos: videosToSend,
      attachments: attachmentsToSend,
      property_status_id: 1,
      property_type_id: 1,
      purpose_id: 1,
      user_id: currentUserId,
    };

    setUploading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/property/lead/add`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.statusType === "SUCCESS") {
        showToast("Post added successfully", { type: "success" });
        setTimeout(() => {
          navigate("/post");
        }, 1500);
      } else {
        showToast(response.data.text || "Failed to submit Post data", { type: "error" });
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to add Post data", { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        service_id: "",
        service: "",
        state: "",
        state_name: "",
        city: "",
        city_name: "",
        description: "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values, isSubmitting }) => (
        <div className={styles.container}>
          <Notification />

          <div className={styles.headerBlock}>
            <h1 className={styles.title}>Create New Post</h1>
            <p className={styles.subtitle}>
              Fill in the service details and location below to share your post with the community.
            </p>
          </div>

          <div className={styles.card}>
            {showPreview ? (
              <div className="preview-section">
                <h3 className="fw-bold mb-3" style={{ color: "#0f172a" }}>Preview Your Post</h3>
                <div className={styles.previewCard}>
                  <div className="mb-2">
                    <strong>Service:</strong> {previewData.service}
                  </div>
                  <div className="mb-2">
                    <strong>Location:</strong> {previewData.city_name}, {previewData.state_name}
                  </div>
                  <div className="mb-2">
                    <strong>Description:</strong>
                    <div
                      dangerouslySetInnerHTML={{ __html: previewData.description }}
                      className="p-3 bg-white border rounded mt-2"
                    />
                  </div>
                  {(images.length > 0 || videos.length > 0) && (
                    <div className="mb-2">
                      <strong>Media:</strong>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {images.map((img, i) => (
                          <img
                            key={i}
                            src={img.image_url}
                            alt={`Preview ${i}`}
                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                            className="rounded border"
                          />
                        ))}
                        {videos.map((vid, i) => (
                          <video
                            key={`v-${i}`}
                            src={vid.image_url}
                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                            className="rounded border"
                            controls
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="d-flex justify-content-between mt-4">
                  <button type="button" className={styles.btnBack} onClick={() => setShowPreview(false)}>
                    <FiArrowLeft size={16} /> Back to Edit
                  </button>
                  <button
                    type="button"
                    className={styles.btnSubmit}
                    onClick={handleFinalSubmit}
                    disabled={uploading}
                  >
                    {uploading ? "Submitting…" : <><FiCheck size={16} /> Confirm & Submit</>}
                  </button>
                </div>
              </div>
            ) : (
              <Form>
                {/* Service / State / City Grid */}
                <Row className="g-4 mb-4">
                  <Col md={4}>
                    <div className="form-group">
                      <label htmlFor="service_id" className={styles.fieldLabel}>
                        <FiLayers size={14} /> Service
                      </label>
                      <Field
                        as="select"
                        name="service_id"
                        id="service_id"
                        className={styles.selectInput}
                        value={values.service_id}
                        onChange={(e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const name = selectedOption.getAttribute("data-name");
                          setFieldValue("service_id", e.target.value);
                          setFieldValue("service", name);
                        }}
                      >
                        <option value="">Select a service</option>
                        {services.map((srv) => (
                          <option key={srv.id} value={srv.id} data-name={srv.name}>
                            {srv.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="service" component="div" className="text-danger mt-1 small" />
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="form-group">
                      <label htmlFor="state" className={styles.fieldLabel}>
                        <FiMapPin size={14} /> State
                      </label>
                      <Field
                        as="select"
                        name="state"
                        id="state"
                        className={styles.selectInput}
                        value={values.state}
                        onChange={async (e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const name = selectedOption.getAttribute("data-name");
                          const stateId = e.target.value;
                          setFieldValue("state", stateId);
                          setFieldValue("state_name", name);
                          await fetchCities(stateId, setFieldValue);
                        }}
                      >
                        <option value="">Select a state</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.id} data-name={state.name}>
                            {state.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="state" component="div" className="text-danger mt-1 small" />
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="form-group">
                      <label htmlFor="city" className={styles.fieldLabel}>
                        <FiMapPin size={14} /> City
                      </label>
                      <Field
                        as="select"
                        name="city"
                        id="city"
                        className={styles.selectInput}
                        value={values.city}
                        onChange={(e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const name = selectedOption.getAttribute("data-name");
                          setFieldValue("city", e.target.value);
                          setFieldValue("city_name", name);
                        }}
                      >
                        <option value="">Select a city</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id} data-name={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="city" component="div" className="text-danger mt-1 small" />
                    </div>
                  </Col>
                </Row>

                {/* Description */}
                <div className="mb-4">
                  <label htmlFor="description" className={styles.fieldLabel}>
                    <FiFileText size={14} /> Description
                  </label>
                  <div className={styles.quillWrapper}>
                    <ReactQuill
                      theme="snow"
                      value={description}
                      onChange={(value) => {
                        setDescription(value);
                        setFieldValue("description", value);
                        setCharacterCount(value.length);
                      }}
                    />
                  </div>
                  <div className={styles.charCounter}>
                    <span>{characterCount}/{MAX_DESCRIPTION_LENGTH} characters</span>
                    {characterCount > MAX_DESCRIPTION_LENGTH && (
                      <span className="text-danger font-semibold">Description too long</span>
                    )}
                  </div>
                  <ErrorMessage name="description" component="div" className="text-danger mt-1 small" />
                </div>

                {/* Upload Media */}
                <div className="mb-4">
                  <UploadMedia
                    images={images}
                    setImages={setImages}
                    videos={videos}
                    setVideos={setVideos}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    label="Upload images, videos, or PDFs"
                  />
                </div>

                {/* Actions: Cancel & Submit */}
                <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                  <button
                    type="button"
                    className={styles.btnBack}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.btnSubmit}
                    disabled={isSubmitting || uploading || characterCount > MAX_DESCRIPTION_LENGTH}
                  >
                    {uploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Posting…
                      </>
                    ) : (
                      <>
                        <FiSend size={16} /> Submit Post
                      </>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </div>
        </div>
      )}
    </Formik>
  );
};

export default AddPostForm;